/* global FormData */
import React, { Component } from "react";
import PropTypes from "prop-types";
import isJson from "is-json";
import utils from "../../lib/utils";
import api from "../../api";
import { uploadFile, uploadLargeFile } from "../../lib/uploads";
import accepts from "attr-accept";
import forEach from "lodash.foreach";
import cn from "classnames";
import Icon from "../Icon";
import { DropTarget } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";

class Files extends Component {
  static propTypes = {
    value: PropTypes.array.isRequired,
    onChange: PropTypes.func,
    limit: PropTypes.number,
    labels: PropTypes.object,
    accepted: PropTypes.string,
    forWeb: PropTypes.bool,
    publicUrl: PropTypes.bool,
  };

  static defaultProps = {
    limit: 0,
    accepted: "",
    labels: {
      one: "a file",
      multiple: "files",
      upload: "Pick or drop",
    },
  };

  state = {
    files: this.getValue(this.props),
  };

  componentWillReceiveProps(props) {
    if (props.value !== this.props.value) {
      this.setState({ files: this.getValue(props) });
    }
  }

  canAddFiles() {
    const { files } = this.state;
    const { limit } = this.props;

    return files.length < limit || limit === 0;
  }

  uploadFiles = async (files) => {
    const { limit, accepted, publicUrl, forWeb } = this.props;

    const uploads = files.filter((file, i) => {
      if (this.state.files.length + (i + 1) > limit && limit > 0) return false;
      if (accepted && !accepts(file, accepted)) return false;
      return true;
    });

    this.setState({
      files: this.state.files.concat(
        uploads.map((file) => ({
          key: file.name,
          name: file.name,
          status: "Loading..",
          loading: true,
          error: null,
        }))
      ),
    });

    await Promise.all(
      uploads.map(async (file) => {
        try {
          const res = forWeb
            ? await uploadFile({ file, publicUrl, forWeb })
            : await uploadLargeFile({ file, publicUrl });

          if (res.status === "error") {
            this.onFailure(file.name, { message: res.message });
          } else {
            const upload = { key: file.name, name: res.name, size: file.size };
            if (res.url) {
              this.onSuccess({ ...upload, url: res.url });
            } else {
              this.onSuccess(upload);
            }
          }
        } catch (err) {
          this.onFailure(file.name, err);
        }
      })
    );
  };

  onSuccess(successFile) {
    const { files } = this.state;
    const index = files.findIndex(
      (f) => f.key === successFile.key || f.name === successFile.name
    );
    if (index < 0) return;

    files[index] = { ...successFile, loading: false, status: "Complete" };
    this.setValue(files);
  }

  onFailure(key, err) {
    const { files } = this.state;
    const index = files.findIndex((f) => f.key === key);
    if (index < 0) return;

    files[index] = {
      ...files[index],
      status: "Error",
      error: err.message,
      loading: false,
    };
    this.setValue(files);
  }

  onRemove(file, e) {
    e.preventDefault();
    const files = this.state.files.filter((f) => f.name !== file.name);
    this.setValue(files);
  }

  // parse raw value from API response
  // Accepts:
  // - single value (array/object)
  // - array of objects
  // - array of strings
  getValue(props) {
    if (!props.value) return [];
    const value = [].concat(props.value);
    const first = value.slice(0, 1).shift();
    if (typeof first === "string" && !isJson(first)) {
      const [name, size] = value;
      return [{ name, size }];
    }
    return value.map(this.fileFromValue);
  }

  fileFromValue(value) {
    if (typeof value === "string" && isJson(value)) {
      return JSON.parse(value);
    }

    return value;
  }

  setValue(value) {
    const nextValue = value.map((f) => {
      const value = { name: f.name, size: parseInt(f.size) };
      return f.url ? { ...value, url: f.url } : value;
    });
    // const nextValue = value.map(f => ({...f, size: parseInt(f.size)}))
    this.setState({ files: value }, () => this.props.onChange(nextValue));
  }

  onFileChange = (e) => {
    e.preventDefault();
    this.uploadFiles([...e.currentTarget.files]);
    e.currentTarget.value = "";
  };

  renderFile = (file) => {
    if (this.props.renderFile) return this.props.renderFile(file);

    return (
      <li
        key={file.key || file.name}
        className={cn("content-item", { error: !!file.error })}
      >
        <div className="main-action">
          <a className="list-title" target="_blank" href={utils.fileUrl(file)}>
            {file.name}
          </a>
          <span className="upload-status">{file.status || ""}</span>
          <span className="upload-error">{file.error || ""}</span>
        </div>
        <div className="buttons">
          <button
            className="ion-ios-close-outline"
            onClick={this.onRemove.bind(this, file)}
          />
        </div>
      </li>
    );
  };

  render() {
    const { limit, labels, accepted, connectDropTarget } = this.props;
    const uploadLabel =
      `${labels.upload} ` + (limit === 1 ? labels.one : labels.multiple);
    const disabled = !this.canAddFiles();

    return connectDropTarget(
      <div className="panel file-picker">
        <ul className="list file-list">
          {this.state.files.map(this.renderFile)}
        </ul>
        <label className={cn("file-upload", { disabled })}>
          <input
            type="file"
            onChange={this.onFileChange}
            disabled={disabled}
            accept={accepted}
          />
          <span className="file-upload-label">
            <Icon name="ios-cloud-upload" />
            {uploadLabel}
          </span>
        </label>
      </div>
    );
  }
}

export default DropTarget(
  NativeTypes.FILE,
  {
    canDrop: (props) => true,
    drop: (props, monitor, component) => {
      const { files } = monitor.getItem();
      if (component.canAddFiles()) component.uploadFiles([...files]);
    },
  },
  (connect) => ({ connectDropTarget: connect.dropTarget() })
)(Files);



// WEBPACK FOOTER //
// ./app/components/fields/Files.js